// deno-lint-ignore-file no-explicit-any
// TODO(PWA migration): remove when the reused planning JS helpers expose TypeScript declarations.
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addIsoDays,
  buildGanttModel,
  buildLookaheadBuckets,
  buildRollingWaveSummary,
  dependencyLabels,
  formatDateRange,
  formatPlanningDate,
  mondayForIsoDate,
  rollingWaveDetailState,
  toIsoDateLocal,
  workWeekForIsoDate,
} from "../../planning/planning-core.js";
import {
  buildHierarchyGroups,
  buildWeekHierarchyGroups,
  withAncestorContext,
} from "../../planning/week-hierarchy.js";
import "../../planning/planning.css";
import { AppShell } from "../components/AppShell";
import { planningApi, PlanningApiError, type PlanningProject } from "../lib/planningApi";

type PlanningView = "gantt" | "week" | "lookahead";
type DialogState =
  | { kind: "create"; parentId: number | null; plan: any }
  | { kind: "schedule"; item: any; plan: any }
  | { kind: "dependency"; item: any; plan: any }
  | null;

const PROJECT_STORAGE_KEY = "dbvdw-planning-project";
const VIEW_STORAGE_KEY = "dbvdw-planning-view";
const VIEWS: PlanningView[] = ["gantt", "week", "lookahead"];

function readView(): PlanningView {
  try {
    const value = localStorage.getItem(VIEW_STORAGE_KEY) as PlanningView | null;
    return value && VIEWS.includes(value) ? value : "gantt";
  } catch {
    return "gantt";
  }
}

function readProject(): string | null {
  try {
    return localStorage.getItem(PROJECT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeSelection(projectId: string | null, view: PlanningView): void {
  try {
    if (projectId) localStorage.setItem(PROJECT_STORAGE_KEY, projectId);
    localStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch { /* local persistence is optional */ }
}

function workPackageMeta(item: any): string {
  return [
    item.type?.name || "Type onbekend",
    item.status?.name,
    item.assignee?.name,
  ].filter(
    Boolean,
  ).join(" · ");
}

function dependencyText(item: any, dependencies: any[]): string {
  const { predecessors, successors } = dependencyLabels(
    item.id,
    dependencies ?? [],
  );
  const parts: string[] = [];
  if (predecessors.length) {
    parts.push(`na ${predecessors.map((id: number) => `#${id}`).join(", ")}`);
  }
  if (successors.length) {
    parts.push(`voor ${successors.map((id: number) => `#${id}`).join(", ")}`);
  }
  return parts.join(" · ");
}

function weekPreview(startDate: string, dueDate: string): string {
  if (!startDate && !dueDate) return "";
  const startLabel = startDate ? workWeekForIsoDate(startDate).label : "";
  const dueLabel = dueDate ? workWeekForIsoDate(dueDate).label : "";
  if (startLabel && dueLabel && startLabel !== dueLabel) {
    return `${startLabel} → ${dueLabel}`;
  }
  return startLabel || dueLabel;
}

function DetailBadge(
  { item, workPackages, lookaheadStart }: {
    item: any;
    workPackages: any[];
    lookaheadStart: string;
  },
) {
  const state = rollingWaveDetailState(
    item,
    workPackages ?? [],
    lookaheadStart,
  );
  if (!state.needsDetail) return null;
  const suffix = state.urgency === "near_term"
    ? " · komende 2 weken"
    : state.urgency === "dates"
    ? " · datums"
    : "";
  return (
    <span
      className={`planning-detail-badge${
        state.urgency === "near_term" ? " planning-detail-badge--urgent" : ""
      }`}
      title={state.reason || "Meer detail nodig"}
    >
      needs_detail{suffix}
    </span>
  );
}

function RollingWaveSummary(
  { workPackages, lookaheadStart, label = "Rolling wave" }: {
    workPackages: any[];
    lookaheadStart: string;
    label?: string;
  },
) {
  const summary = buildRollingWaveSummary(workPackages ?? [], lookaheadStart);
  if (summary.topLevelPhaseCount === 0) return null;
  return (
    <div className="planning-rolling-wave-summary">
      <strong>{label}</strong>
      <span>{summary.topLevelPhaseCount} top-level fases</span>
      <span>{summary.horizonPhaseCount} binnen 12 weken</span>
      <span>{summary.needsDetailCount} needs_detail</span>
      {summary.nearTermNeedsDetailCount > 0 && (
        <span>
          <strong>{summary.nearTermNeedsDetailCount}</strong> binnen komende 2 weken
        </span>
      )}
    </div>
  );
}

function ActionButtons(
  { item, canWrite, onAction }: {
    item: any;
    canWrite: boolean;
    onAction: (kind: "create" | "schedule" | "dependency", item: any) => void;
  },
) {
  if (!canWrite) return null;
  return (
    <div className="planning-item-actions">
      {item.parentId == null && item.type?.milestone !== true && (
        <button
          type="button"
          className="secondary"
          onClick={() => onAction("create", item)}
        >
          Detail toevoegen
        </button>
      )}
      <button
        type="button"
        className="secondary"
        onClick={() => onAction("schedule", item)}
      >
        Datums
      </button>
      <button
        type="button"
        className="secondary"
        onClick={() => onAction("dependency", item)}
      >
        Afhankelijkheid
      </button>
    </div>
  );
}

function PlanningCard(
  { item, readModel, lookaheadStart, canWrite, onAction, children }: {
    item: any;
    readModel: any;
    lookaheadStart: string;
    canWrite: boolean;
    onAction: (kind: "create" | "schedule" | "dependency", item: any) => void;
    children?: ReactNode;
  },
) {
  const dependency = dependencyText(item, readModel.dependencies ?? []);
  return (
    <article className="planning-card">
      <div className="planning-card__heading">
        <strong>#{item.id} {item.subject}</strong>
        <div className="planning-card__badges">
          {item.type?.milestone && <span className="planning-badge">Mijlpaal</span>}
          <DetailBadge
            item={item}
            workPackages={readModel.workPackages ?? []}
            lookaheadStart={lookaheadStart}
          />
        </div>
      </div>
      <div className="planning-card__dates">
        {formatDateRange(item.startDate, item.dueDate)}
      </div>
      <div className="planning-card__meta">{workPackageMeta(item)}</div>
      {dependency && (
        <div className="planning-card__meta">
          Afhankelijkheden: {dependency}
        </div>
      )}
      <ActionButtons item={item} canWrite={canWrite} onAction={onAction} />
      {children}
    </article>
  );
}

function HierarchyCards(
  {
    groups,
    readModel,
    lookaheadStart,
    canWrite,
    onAction,
    emptyText = "Geen geplande werkzaamheden.",
    childLabel = "Subtasks",
  }: {
    groups: any[];
    readModel: any;
    lookaheadStart: string;
    canWrite: boolean;
    onAction: (kind: "create" | "schedule" | "dependency", item: any) => void;
    emptyText?: string;
    childLabel?: string;
  },
) {
  if (!groups.length) {
    return <p className="planning-empty-state">{emptyText}</p>;
  }
  return (
    <div className="planning-card-list">
      {groups.map((group: any) => (
        <PlanningCard
          key={group.root.id}
          item={group.root}
          readModel={readModel}
          lookaheadStart={lookaheadStart}
          canWrite={canWrite}
          onAction={onAction}
        >
          {group.children.length
            ? (
              <>
                <div className="planning-card__meta">
                  <strong>{childLabel}</strong>
                </div>
                <div
                  className="planning-card-list"
                  style={{ gridTemplateColumns: "1fr", marginTop: ".75rem" }}
                >
                  {group.children.map(({ item, depth }: any) => (
                    <div
                      className="planning-card"
                      key={item.id}
                      style={{
                        boxShadow: "none",
                        marginLeft: `${Math.min(depth, 4) * .5}rem`,
                      }}
                    >
                      <div className="planning-card__heading">
                        <strong>↳ #{item.id} {item.subject}</strong>
                        {item.type?.milestone && <span className="planning-badge">Mijlpaal</span>}
                      </div>
                      <div className="planning-card__dates">
                        {formatDateRange(item.startDate, item.dueDate)}
                      </div>
                      <div className="planning-card__meta">
                        {workPackageMeta(item)}
                      </div>
                      <ActionButtons
                        item={item}
                        canWrite={canWrite}
                        onAction={onAction}
                      />
                    </div>
                  ))}
                </div>
              </>
            )
            : (
              <div className="planning-card__meta">
                Geen subtasks in deze periode.
              </div>
            )}
        </PlanningCard>
      ))}
    </div>
  );
}

function GanttView(
  { data, project, lookaheadStart, canWrite, onAction }: {
    data: any;
    project: PlanningProject | null;
    lookaheadStart: string;
    canWrite: boolean;
    onAction: (kind: "create" | "schedule" | "dependency", item: any) => void;
  },
) {
  const model = buildGanttModel(data);
  if (!data.workPackages?.length) {
    return (
      <>
        <div className="planning-view-summary">
          <strong>
            {data.project?.name || project?.displayName || "Project"}
          </strong>
        </div>
        <p className="planning-empty-state">
          Nog geen planningitems beschikbaar. Begin met de bestaande post-its als top-level fases.
        </p>
      </>
    );
  }

  const scheduledRows = new Map(
    model.rows.map((row: any) => [row.item.id, row]),
  );
  const contextItems = withAncestorContext(
    data.workPackages,
    model.rows.map((row: any) => row.item),
  );
  const groups = buildHierarchyGroups(contextItems);
  const rows = groups.flatMap((
    group: any,
  ) => [{ item: group.root, depth: 0 }, ...group.children]);
  const contextIds = new Set(contextItems.map((item: any) => item.id));
  const unscheduled = model.unscheduled.map((row: any) => row.item).filter((
    item: any,
  ) => !contextIds.has(item.id));
  const summary = model.rangeStart
    ? `${formatPlanningDate(model.rangeStart, { short: true })} – ${
      formatPlanningDate(model.rangeEnd, { short: true })
    }`
    : "Nog geen datums gepland";

  return (
    <>
      <div className="planning-view-summary">
        <strong>
          {data.project?.name || project?.displayName || "Project"}
        </strong>
        <span>{summary}</span>
        <span>
          {groups.length} groepen · {unscheduled.length} volledig niet ingepland
        </span>
      </div>
      <RollingWaveSummary
        workPackages={data.workPackages}
        lookaheadStart={lookaheadStart}
      />
      {rows.length
        ? (
          <div
            className="planning-gantt-scroll"
            tabIndex={0}
            aria-label="Gantt planning, horizontaal scrollbaar"
          >
            <div
              className="planning-gantt"
              style={{
                "--timeline-width": `${model.timelineWidth.toFixed(2)}px`,
              } as React.CSSProperties}
            >
              <div className="planning-gantt__row planning-gantt__row--header">
                <div className="planning-gantt__label planning-gantt__label--header">
                  Werkpakket
                </div>
                <div
                  className="planning-gantt__track planning-gantt__track--header"
                  style={{ width: `${model.timelineWidth.toFixed(2)}px` }}
                >
                  {model.months.map((month: any) => (
                    <span
                      className="planning-gantt__month"
                      key={`${month.label}-${month.offsetPercent}`}
                      style={{
                        left: `${month.offsetPercent}%`,
                        width: `${month.widthPercent}%`,
                      }}
                    >
                      {month.label}
                    </span>
                  ))}
                  {model.weeks.map((week: any) => (
                    <span
                      className="planning-gantt__week"
                      key={week.startDate}
                      style={{
                        left: `${week.offsetPercent}%`,
                        width: `${week.widthPercent}%`,
                      }}
                      title={`${week.label} · ${week.startDate} – ${week.endDate}`}
                    >
                      {week.label}
                    </span>
                  ))}
                </div>
              </div>
              {rows.map(({ item, depth }: any) => {
                const scheduled = scheduledRows.get(item.id) as any;
                const dependency = dependencyText(
                  item,
                  data.dependencies ?? [],
                );
                const barClass = item.type?.milestone
                  ? "planning-gantt__bar planning-gantt__bar--milestone"
                  : "planning-gantt__bar";
                return (
                  <div className="planning-gantt__row" key={item.id}>
                    <div
                      className="planning-gantt__label"
                      style={{
                        paddingLeft: `${12 + Math.min(depth, 6) * 18}px`,
                      }}
                    >
                      <strong>
                        {depth > 0 ? "↳ " : ""}#{item.id} {item.subject}
                      </strong>
                      <span>{workPackageMeta(item)}</span>
                      {!scheduled && (
                        <span className="planning-card__meta">
                          Groep · planning volgt uit onderliggende taken
                        </span>
                      )}
                      <DetailBadge
                        item={item}
                        workPackages={data.workPackages}
                        lookaheadStart={lookaheadStart}
                      />
                      {dependency && <span>{dependency}</span>}
                      <ActionButtons
                        item={item}
                        canWrite={canWrite}
                        onAction={onAction}
                      />
                    </div>
                    <div
                      className="planning-gantt__track"
                      style={{ width: `${model.timelineWidth.toFixed(2)}px` }}
                    >
                      {scheduled && (
                        <span
                          className={barClass}
                          style={{
                            left: `${scheduled.left.toFixed(2)}px`,
                            width: `${scheduled.width.toFixed(2)}px`,
                          }}
                          title={formatDateRange(item.startDate, item.dueDate)}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )
        : (
          <p className="planning-empty-state">
            Geen planningitems met een datum.
          </p>
        )}
      <div className="planning-gantt-mobile">
        <HierarchyCards
          groups={groups}
          readModel={data}
          lookaheadStart={lookaheadStart}
          canWrite={canWrite}
          onAction={onAction}
          emptyText="Geen planningitems met een datum."
          childLabel="Onderliggende planning"
        />
      </div>
      {unscheduled.length > 0 && (
        <section className="planning-unscheduled">
          <h3>Nog niet ingepland</h3>
          <div className="planning-card-list">
            {unscheduled.map((item: any) => (
              <PlanningCard
                key={item.id}
                item={item}
                readModel={data}
                lookaheadStart={lookaheadStart}
                canWrite={canWrite}
                onAction={onAction}
              />
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function WeekView(
  { data, project, lookaheadStart, canWrite, onAction }: {
    data: any;
    project: PlanningProject | null;
    lookaheadStart: string;
    canWrite: boolean;
    onAction: (kind: "create" | "schedule" | "dependency", item: any) => void;
  },
) {
  const groups = buildWeekHierarchyGroups(data.workPackages ?? []);
  const children = groups.reduce(
    (sum: number, group: any) => sum + group.children.length,
    0,
  );
  return (
    <>
      <div className="planning-view-summary">
        <strong>
          {data.project?.name || project?.displayName || "Project"}
        </strong>
        <span className="planning-work-week">
          {workWeekForIsoDate(data.startDate).label}
        </span>
        <span>
          {formatPlanningDate(data.startDate, { short: true })} –{" "}
          {formatPlanningDate(data.endDate, { short: true })}
        </span>
        <span>
          {groups.length} groepen · {children} subtasks · {data.unscheduledCount}{" "}
          niet ingepland in project
        </span>
      </div>
      <HierarchyCards
        groups={groups}
        readModel={data}
        lookaheadStart={lookaheadStart}
        canWrite={canWrite}
        onAction={onAction}
        emptyText="Geen geplande werkzaamheden in deze werkweek."
        childLabel="Subtasks deze week"
      />
    </>
  );
}

function LookaheadView(
  { data, project, lookaheadStart, canWrite, onAction }: {
    data: any;
    project: PlanningProject | null;
    lookaheadStart: string;
    canWrite: boolean;
    onAction: (kind: "create" | "schedule" | "dependency", item: any) => void;
  },
) {
  const buckets = buildLookaheadBuckets(data);
  const horizon = withAncestorContext(
    data.workPackages ?? [],
    buckets.flatMap((week: any) => week.items),
  );
  const horizonGroups = buildHierarchyGroups(horizon);
  const horizonChildren = horizonGroups.reduce(
    (sum: number, group: any) => sum + group.children.length,
    0,
  );
  return (
    <>
      <div className="planning-view-summary">
        <strong>
          {data.project?.name || project?.displayName || "Project"}
        </strong>
        <span>{buckets[0]?.label || ""}</span>
        <span>
          {formatPlanningDate(data.startDate, { short: true })} –{" "}
          {formatPlanningDate(data.endDate, { short: true })}
        </span>
        <span>
          {horizonGroups.length} groepen · {horizonChildren} subtasks · {data.unscheduledCount}{" "}
          niet ingepland in project
        </span>
      </div>
      <RollingWaveSummary
        workPackages={data.workPackages ?? []}
        lookaheadStart={lookaheadStart}
        label="Binnen deze 12 weken"
      />
      <div className="planning-lookahead-grid">
        {buckets.map((week: any) => {
          const groups = buildHierarchyGroups(
            withAncestorContext(data.workPackages ?? [], week.items),
          );
          const children = groups.reduce(
            (sum: number, group: any) => sum + group.children.length,
            0,
          );
          return (
            <section className="planning-week-card" key={week.startDate}>
              <header className="planning-week-card__header">
                <div>
                  <strong>{week.label}</strong>
                  <span
                    className="planning-card__meta"
                    style={{ display: "block", marginTop: ".2rem" }}
                  >
                    {groups.length} groepen · {children} subtasks
                  </span>
                </div>
                <span>
                  {formatPlanningDate(week.startDate, {
                    short: true,
                    withYear: false,
                  })} – {formatPlanningDate(week.endDate, { short: true })}
                </span>
              </header>
              <HierarchyCards
                groups={groups}
                readModel={data}
                lookaheadStart={lookaheadStart}
                canWrite={canWrite}
                onAction={onAction}
                emptyText="Geen geplande werkzaamheden."
                childLabel="Subtasks deze week"
              />
            </section>
          );
        })}
      </div>
    </>
  );
}

function ModalDialog(
  { title, intro, onClose, children }: {
    title: string;
    intro?: ReactNode;
    onClose: () => void;
    children: ReactNode;
  },
) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    const close = () => onClose();
    dialog.addEventListener("cancel", close);
    return () => dialog.removeEventListener("cancel", close);
  }, [onClose]);
  return (
    <dialog ref={ref} className="planning-dialog">
      <div className="planning-dialog__heading">
        <div>
          <h2>{title}</h2>
          {intro}
        </div>
        <button
          type="button"
          className="secondary"
          onClick={onClose}
          aria-label="Sluiten"
        >
          ×
        </button>
      </div>
      {children}
    </dialog>
  );
}

export function PlanningPage() {
  const [projects, setProjects] = useState<PlanningProject[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [view, setView] = useState<PlanningView>(readView);
  const [weekStart, setWeekStart] = useState(() => mondayForIsoDate(toIsoDateLocal()));
  const lookaheadStart = useMemo(() => mondayForIsoDate(toIsoDateLocal()), []);
  const [data, setData] = useState<any>(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingView, setLoadingView] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [writeBusy, setWriteBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const currentProject = projects.find((project) => project.dbvdwProjectId === selectedProjectId) ??
    null;
  const canWrite = currentProject?.canWrite === true;

  useEffect(() => {
    const controller = new AbortController();
    void planningApi.listProjects(controller.signal).then((nextProjects) => {
      if (controller.signal.aborted) return;
      setProjects(nextProjects);
      if (nextProjects.length) {
        const stored = readProject();
        setSelectedProjectId(
          nextProjects.some((project) => project.dbvdwProjectId === stored)
            ? stored
            : nextProjects[0].dbvdwProjectId,
        );
      }
    }).catch((cause) => {
      if (!controller.signal.aborted) {
        setError(
          cause instanceof Error ? cause.message : "Planning kon niet worden geladen.",
        );
      }
    }).finally(() => {
      if (!controller.signal.aborted) setLoadingProjects(false);
    });
    return () => controller.abort();
  }, []);

  const loadView = useCallback(async (signal?: AbortSignal) => {
    if (!selectedProjectId) return;
    setLoadingView(true);
    setError(null);
    setStatus("Planning laden...");
    try {
      const next = view === "gantt"
        ? await planningApi.getPlan(selectedProjectId, signal)
        : view === "week"
        ? await planningApi.getWeek(selectedProjectId, weekStart, signal)
        : await planningApi.getLookahead(
          selectedProjectId,
          lookaheadStart,
          signal,
        );
      setData(next);
      setStatus(
        `Bijgewerkt ${
          new Date().toLocaleTimeString("nl-NL", {
            hour: "2-digit",
            minute: "2-digit",
          })
        }`,
      );
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setError(
        cause instanceof Error ? cause.message : "Planning kon niet worden geladen.",
      );
      setStatus("Laden mislukt");
    } finally {
      setLoadingView(false);
    }
  }, [selectedProjectId, view, weekStart, lookaheadStart]);

  useEffect(() => {
    if (!selectedProjectId) return;
    storeSelection(selectedProjectId, view);
    const controller = new AbortController();
    void loadView(controller.signal);
    return () => controller.abort();
  }, [selectedProjectId, view, weekStart, loadView]);

  async function openAction(
    kind: "create" | "schedule" | "dependency",
    item: any | null,
  ) {
    if (!selectedProjectId || !canWrite) return;
    setError(null);
    setFormError(null);
    setStatus("Actuele planning laden...");
    try {
      const plan = await planningApi.getPlan(selectedProjectId);
      if (kind === "create") {
        if (item && (item.parentId != null || item.type?.milestone === true)) {
          throw new Error(
            "Detail kan alleen direct onder een top-level fase worden toegevoegd.",
          );
        }
        setDialog({ kind: "create", parentId: item?.id ?? null, plan });
      } else {
        const fresh = plan.workPackages?.find((candidate: any) => candidate.id === item.id);
        if (!fresh) {
          throw new Error("Planningitem niet gevonden. Ververs de planning.");
        }
        setDialog({ kind, item: fresh, plan } as DialogState);
      }
      setStatus("");
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Formulier kon niet worden geopend.",
      );
    }
  }

  async function confirmedWrite(action: () => Promise<unknown>) {
    if (writeBusy) return;
    setWriteBusy(true);
    setFormError(null);
    setError(null);
    setStatus("Planningwijziging opslaan...");
    try {
      await action();
      setDialog(null);
      await loadView();
      setStatus("Planningwijziging bevestigd");
    } catch (cause) {
      if (
        cause instanceof PlanningApiError &&
        ["conflict", "unreachable", "invalid_response"].includes(cause.code)
      ) {
        setDialog(null);
        try {
          await loadView();
        } catch { /* original error remains primary */ }
        setError(`Planningwijziging niet bevestigd: ${cause.message}`);
        setStatus("Wijziging niet bevestigd");
      } else {
        setFormError(
          cause instanceof Error ? cause.message : "Planningwijziging mislukt.",
        );
        setStatus("Wijziging geweigerd");
      }
    } finally {
      setWriteBusy(false);
    }
  }

  return (
    <AppShell>
      <main className="panel planning-panel">
        <div className="planning-heading">
          <div>
            <h1>Planning</h1>
            <p className="muted">
              Projectplanning uit OpenProject via de beveiligde DBVDW planning-adapter.
            </p>
          </div>
          {currentProject && (
            <span
              className={canWrite ? "planning-write-badge" : "planning-readonly-badge"}
            >
              {canWrite ? "Bewerken toegestaan" : "Alleen lezen"}
            </span>
          )}
        </div>

        {loadingProjects && (
          <section id="planning-loading">
            <p>Planningprojecten laden...</p>
          </section>
        )}
        {!loadingProjects && !projects.length && !error && (
          <div className="placeholder-panel">
            <h2>Geen planning beschikbaar</h2>
            <p>
              Voor jouw huidige projectrechten is nog geen planningproject gekoppeld.
            </p>
          </div>
        )}
        {error && <section className="planning-error" role="alert">{error}</section>}

        {!loadingProjects && projects.length > 0 && selectedProjectId && (
          <section>
            <div className="planning-toolbar">
              <label>
                <span>Project</span>
                <select
                  value={selectedProjectId}
                  disabled={loadingView || writeBusy}
                  onChange={(event) => setSelectedProjectId(event.target.value)}
                >
                  {projects.map((project) => (
                    <option
                      value={project.dbvdwProjectId}
                      key={project.dbvdwProjectId}
                    >
                      {project.displayName} ({project.projectCode})
                    </option>
                  ))}
                </select>
              </label>
              {canWrite && (
                <button
                  type="button"
                  disabled={loadingView || writeBusy}
                  onClick={() => void openAction("create", null)}
                >
                  Nieuwe fase
                </button>
              )}
              <button
                type="button"
                className="secondary"
                disabled={loadingView || writeBusy}
                onClick={() => void loadView()}
              >
                Verversen
              </button>
              <span className="muted" aria-live="polite">{status}</span>
            </div>

            <div
              className="planning-tabs"
              role="group"
              aria-label="Planningweergave"
            >
              {([["gantt", "Gantt"], ["week", "Week"], [
                "lookahead",
                "12 weken",
              ]] as Array<
                [PlanningView, string]
              >).map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  aria-pressed={view === key}
                  onClick={() => setView(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="planning-view-heading">
              <h2>
                {view === "gantt"
                  ? "Projectplanning"
                  : view === "week"
                  ? "Weekplanning"
                  : "12-weeks vooruitblik"}
              </h2>
              {view === "week" && (
                <div
                  className="planning-week-controls"
                  aria-label="Week kiezen"
                >
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setWeekStart((current) => addIsoDays(current, -7))}
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setWeekStart(mondayForIsoDate(toIsoDateLocal()))}
                  >
                    Deze week
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setWeekStart((current) => addIsoDays(current, 7))}
                  >
                    →
                  </button>
                </div>
              )}
            </div>

            {loadingView && !data && <p>Planning laden...</p>}
            {data && view === "gantt" && (
              <GanttView
                data={data}
                project={currentProject}
                lookaheadStart={lookaheadStart}
                canWrite={canWrite}
                onAction={(kind, item) => void openAction(kind, item)}
              />
            )}
            {data && view === "week" && (
              <WeekView
                data={data}
                project={currentProject}
                lookaheadStart={lookaheadStart}
                canWrite={canWrite}
                onAction={(kind, item) => void openAction(kind, item)}
              />
            )}
            {data && view === "lookahead" && (
              <LookaheadView
                data={data}
                project={currentProject}
                lookaheadStart={lookaheadStart}
                canWrite={canWrite}
                onAction={(kind, item) => void openAction(kind, item)}
              />
            )}
          </section>
        )}
      </main>

      {dialog?.kind === "create" && (
        <CreateDialog
          state={dialog}
          busy={writeBusy}
          error={formError}
          onClose={() => setDialog(null)}
          onSubmit={(input) =>
            selectedProjectId &&
            confirmedWrite(() => planningApi.createWorkPackage(selectedProjectId, input))}
        />
      )}
      {dialog?.kind === "schedule" && (
        <ScheduleDialog
          state={dialog}
          busy={writeBusy}
          error={formError}
          onClose={() => setDialog(null)}
          onSubmit={(input) =>
            selectedProjectId &&
            confirmedWrite(() =>
              planningApi.updateSchedule(
                selectedProjectId,
                dialog.item.id,
                input,
              )
            )}
        />
      )}
      {dialog?.kind === "dependency" && (
        <DependencyDialog
          state={dialog}
          busy={writeBusy}
          error={formError}
          onClose={() => setDialog(null)}
          onSubmit={(input) =>
            selectedProjectId &&
            confirmedWrite(() => planningApi.createDependency(selectedProjectId, input))}
        />
      )}
    </AppShell>
  );
}

function CreateDialog({
  state,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  state: Extract<DialogState, { kind: "create" }>;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (
    input: {
      subject: string;
      typeId: number;
      parentId: number | null;
      startDate: string | null;
      dueDate: string | null;
    },
  ) => void;
}) {
  const [subject, setSubject] = useState("");
  const [typeId, setTypeId] = useState(() => Number(state.plan.types?.[0]?.id ?? 0));
  const [parentId] = useState<number | null>(state.parentId);
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  function submit(event: FormEvent) {
    event.preventDefault();
    onSubmit({
      subject,
      typeId,
      parentId,
      startDate: startDate || null,
      dueDate: dueDate || null,
    });
  }
  return (
    <ModalDialog
      title="Planningitem toevoegen"
      intro={
        <p className="muted">
          Voeg een top-level fase toe, of detail direct onder een bestaande fase.
        </p>
      }
      onClose={onClose}
    >
      <form className="planning-form" onSubmit={submit}>
        <label>
          <span>Omschrijving</span>
          <input
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            maxLength={255}
            required
            disabled={busy}
          />
        </label>
        <label>
          <span>Type</span>
          <select
            value={typeId}
            onChange={(event) => setTypeId(Number(event.target.value))}
            disabled={busy}
            required
          >
            {(state.plan.types ?? []).map((type: any) => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Parent / fase</span>
          <select value={parentId ?? ""} disabled>
            <option value="">Geen parent — top-level fase</option>
            {(state.plan.workPackages ?? []).map((item: any) => (
              <option key={item.id} value={item.id}>
                #{item.id} {item.subject}
              </option>
            ))}
          </select>
        </label>
        <div className="planning-form__dates">
          <label>
            <span>Startdatum</span>
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              disabled={busy}
            />
          </label>
          <label>
            <span>Einddatum</span>
            <input
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              disabled={busy}
            />
          </label>
        </div>
        <p className="planning-form__week muted">
          {weekPreview(startDate, dueDate)}
        </p>
        {error && <p className="planning-form__error" role="alert">{error}</p>}
        <div className="planning-form__actions">
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={onClose}
          >
            Annuleren
          </button>
          <button type="submit" disabled={busy}>Toevoegen</button>
        </div>
      </form>
    </ModalDialog>
  );
}

function ScheduleDialog({
  state,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  state: Extract<DialogState, { kind: "schedule" }>;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (
    input: { expectedLockVersion: number; startDate: string; dueDate: string },
  ) => void;
}) {
  const [startDate, setStartDate] = useState(state.item.startDate ?? "");
  const [dueDate, setDueDate] = useState(state.item.dueDate ?? "");
  function submit(event: FormEvent) {
    event.preventDefault();
    onSubmit({
      expectedLockVersion: Number(state.item.lockVersion),
      startDate,
      dueDate,
    });
  }
  return (
    <ModalDialog
      title="Planningdatums wijzigen"
      intro={<p className="muted">#{state.item.id} {state.item.subject}</p>}
      onClose={onClose}
    >
      <form className="planning-form" onSubmit={submit}>
        <div className="planning-form__dates">
          <label>
            <span>Startdatum</span>
            <input
              type="date"
              required
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              disabled={busy}
            />
          </label>
          <label>
            <span>Einddatum</span>
            <input
              type="date"
              required
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              disabled={busy}
            />
          </label>
        </div>
        <p className="planning-form__week muted">
          {weekPreview(startDate, dueDate)}
        </p>
        <p className="muted">
          Bij een tussentijdse wijziging door iemand anders wordt opslaan geweigerd en moet de
          planning worden ververst.
        </p>
        {error && <p className="planning-form__error" role="alert">{error}</p>}
        <div className="planning-form__actions">
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={onClose}
          >
            Annuleren
          </button>
          <button type="submit" disabled={busy}>Datums opslaan</button>
        </div>
      </form>
    </ModalDialog>
  );
}

function DependencyDialog({
  state,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  state: Extract<DialogState, { kind: "dependency" }>;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (
    input: {
      fromWorkPackageId: number;
      toWorkPackageId: number;
      lagDays: number;
    },
  ) => void;
}) {
  const targets = (state.plan.workPackages ?? []).filter((candidate: any) =>
    candidate.id !== state.item.id
  );
  const [targetId, setTargetId] = useState(() => Number(targets[0]?.id ?? 0));
  const [lagDays, setLagDays] = useState(0);
  function submit(event: FormEvent) {
    event.preventDefault();
    onSubmit({
      fromWorkPackageId: Number(state.item.id),
      toWorkPackageId: targetId,
      lagDays,
    });
  }
  return (
    <ModalDialog
      title="Afhankelijkheid toevoegen"
      intro={<p className="muted">#{state.item.id} {state.item.subject}</p>}
      onClose={onClose}
    >
      <form className="planning-form" onSubmit={submit}>
        <label>
          <span>Dit werkpakket moet vóór</span>
          <select
            required
            value={targetId}
            onChange={(event) => setTargetId(Number(event.target.value))}
            disabled={busy}
          >
            {targets.map((candidate: any) => (
              <option key={candidate.id} value={candidate.id}>
                #{candidate.id} {candidate.subject}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Wachtdagen</span>
          <input
            type="number"
            min={0}
            max={3650}
            step={1}
            value={lagDays}
            onChange={(event) => setLagDays(Number(event.target.value))}
            required
            disabled={busy}
          />
        </label>
        {error && <p className="planning-form__error" role="alert">{error}</p>}
        <div className="planning-form__actions">
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={onClose}
          >
            Annuleren
          </button>
          <button type="submit" disabled={busy}>
            Afhankelijkheid toevoegen
          </button>
        </div>
      </form>
    </ModalDialog>
  );
}
